import { createBrowserRouter, createMemoryRouter, Outlet } from "react-router";
import type { RouteObject } from "react-router";
import { RouteGuard } from "./guards/RouteGuard";
import { NotFoundPage } from "../pages/errors/NotFoundPage";
import { RouteErrorPage } from "../pages/errors/RouteErrorPage";
import { AppShell } from "./layout/AppShell";
import { RootLayout } from "./layout/RootLayout";
import { paths } from "./paths";
import type { RouteHandle } from "./paths";

const handle = (title: string): RouteHandle => ({ title });

/**
 * Route table (WEB-004). Pages load lazily; error pages are imported eagerly so they still
 * render when a page chunk fails to download. Protected routes wrap their element in
 * <RouteGuard policy={…}> — the first ones arrive with sign-in in B01.07.
 */
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        errorElement: <RouteErrorPage />,
        children: [
          {
            // Pages inside the application shell (sidebar, header, <main>).
            element: <AppShell />,
            children: [
              {
                path: paths.home,
                handle: handle("Home"),
                lazy: async () => ({
                  Component: (await import("../pages/home/HomePage")).default,
                }),
              },
              {
                // ORG-EPIC (B02.06): first real use of RouteGuard — cosmetic only, the API
                // enforces `org:read` itself.
                element: (
                  <RouteGuard policy={{ permissions: ["org:read"] }}>
                    <Outlet />
                  </RouteGuard>
                ),
                children: [
                  {
                    path: paths.organizationAdmin,
                    handle: handle("Manage organization"),
                    lazy: async () => ({
                      Component: (await import("../pages/organization-admin/OrganizationAdminPage"))
                        .default,
                    }),
                  },
                  {
                    path: paths.organizationAdminWithId,
                    handle: handle("Manage organization"),
                    lazy: async () => ({
                      Component: (await import("../pages/organization-admin/OrganizationAdminPage"))
                        .default,
                    }),
                  },
                ],
              },
              {
                // PAT-EPIC (B03.02): cosmetic only, the API enforces `patient:read` itself.
                element: (
                  <RouteGuard policy={{ permissions: ["patient:read"] }}>
                    <Outlet />
                  </RouteGuard>
                ),
                children: [
                  {
                    path: paths.patients,
                    handle: handle("Patients"),
                    lazy: async () => ({
                      Component: (await import("../pages/patients/PatientsListPage")).default,
                    }),
                  },
                  {
                    path: paths.patientDetail,
                    handle: handle("Patient"),
                    lazy: async () => ({
                      Component: (await import("../pages/patients/PatientDetailPage")).default,
                    }),
                  },
                ],
              },
              {
                // DS-EPIC (B07.02): cosmetic only, the API enforces `dataset:read` itself.
                element: (
                  <RouteGuard policy={{ permissions: ["dataset:read"] }}>
                    <Outlet />
                  </RouteGuard>
                ),
                children: [
                  {
                    path: paths.datasets,
                    handle: handle("Datasets"),
                    lazy: async () => ({
                      Component: (await import("../pages/datasets/DatasetsListPage")).default,
                    }),
                  },
                  {
                    path: paths.datasetDetail,
                    handle: handle("Dataset"),
                    lazy: async () => ({
                      Component: (await import("../pages/datasets/DatasetDetailPage")).default,
                    }),
                  },
                ],
              },
              {
                // AIX-EPIC (B09.02): cosmetic only, the API enforces `ai:model_manage` itself.
                element: (
                  <RouteGuard policy={{ permissions: ["ai:model_manage"] }}>
                    <Outlet />
                  </RouteGuard>
                ),
                children: [
                  {
                    path: paths.aiModels,
                    handle: handle("AI model registry"),
                    lazy: async () => ({
                      Component: (await import("../pages/ai-models/AiModelsPage")).default,
                    }),
                  },
                ],
              },
              {
                // Platform-level, not org-scoped: `system:configure` is super_admin-only.
                element: (
                  <RouteGuard policy={{ permissions: ["system:configure"] }}>
                    <Outlet />
                  </RouteGuard>
                ),
                children: [
                  {
                    path: paths.platformAdmin,
                    handle: handle("Platform administration"),
                    lazy: async () => ({
                      Component: (await import("../pages/platform-admin/PlatformAdminPage")).default,
                    }),
                  },
                ],
              },
              // "Coming soon" marketing pages (no RouteGuard, same as Home) -- real content is
              // deferred (see docs/DATABASE_SCHEMA.md's platformConfig/features), not gated yet.
              {
                path: paths.secondOpinions,
                handle: handle("Ask the doctor community"),
                lazy: async () => ({
                  Component: (await import("../pages/coming-soon/SecondOpinionsPage")).default,
                }),
              },
              {
                path: paths.doctorNetwork,
                handle: handle("Doctor network"),
                lazy: async () => ({
                  Component: (await import("../pages/coming-soon/DoctorNetworkPage")).default,
                }),
              },
              {
                path: paths.meetingsTrips,
                handle: handle("Meetings & trips"),
                lazy: async () => ({
                  Component: (await import("../pages/coming-soon/MeetingsTripsPage")).default,
                }),
              },
            ],
          },
          // Standalone pages (own full-page layout): sign-in and not-found.
          {
            path: paths.signIn,
            handle: handle("Sign in"),
            lazy: async () => ({
              Component: (await import("../pages/sign-in/SignInPage")).default,
            }),
          },
          {
            path: paths.signUp,
            handle: handle("Create your account"),
            lazy: async () => ({
              Component: (await import("../pages/sign-up/SignUpPage")).default,
            }),
          },
          {
            path: paths.resetPassword,
            handle: handle("Reset password"),
            lazy: async () => ({
              Component: (await import("../pages/reset-password/ResetPasswordPage")).default,
            }),
          },
          {
            path: paths.acceptInvitation,
            handle: handle("Accept your invitation"),
            lazy: async () => ({
              Component: (await import("../pages/accept-invitation/AcceptInvitationPage")).default,
            }),
          },
          { path: "*", handle: handle("Page not found"), element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}

/** For tests: same route table, in-memory history. */
export function createTestRouter(initialPath: string) {
  return createMemoryRouter(routes, { initialEntries: [initialPath] });
}
